import { TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useCustomTranslations } from '../../../../hooks/useCustomTranslations';
import useRegistration from '../../../../hooks/useRegistration';
import { RegistrationField } from '../../../../interfaces/interfaces';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { RegistrationFormFieldProps } from '../RegistrationFormField';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
interface RegistrationEmailFieldInterface
  extends Omit<RegistrationFormFieldProps, 'control' | 'watch' | 'requiredFieldIds' | 'updateRequiredFieldIds' | 'field'> {
  field?: RegistrationField;
  textFieldSx?: any;
  hideGeneratedEmail?: boolean;
}
const RegistrationEmailField = ({
  field: registrationField,
  disabled,
  fieldsTextColor,
  fieldsBorderColor,
  isGuestInviteForm,
  withOAuth,
  textFieldSx,
  showFieldLabel,
  skipUserAlreadyRegisteredCheck,
  variant = 'outlined',
  defaultValue,
  onChange,
  allowEmptyEmail = false,
  showEmailConfirmation,
  onBlur,
  hideGeneratedEmail,
}: RegistrationEmailFieldInterface) => {
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);

  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();

  const eventRegistration = useSelector((state) => state.common.eventRegistration);

  const { ct } = useCustomTranslations();
  const { t } = useTranslation();
  const { isEventVystemEmail, isEventImportEmail } = useRegistration();
  const label = registrationField?.label ? ct(registrationField.label, registrationField, 'labelTranslations') : '';

  const [userEmail, setUserEmail] = useState<string>();

  const getFieldDisabledStatus = (): boolean => {
    if (registrationField?.canEditInProfile) return false;
    else return disabled || withOAuth || (!!eventRegistration && !isGuestInviteForm && !skipUserAlreadyRegisteredCheck);
  };

  useEffect(() => {
    if (defaultValue) {
      setUserEmail(defaultValue);
      setValue('email', defaultValue);
      return;
    }
    if (!user) return;

    if (!isEventVystemEmail(user.email) && !isGuestInviteForm) {
      if (isEventImportEmail(user.email) && hideGeneratedEmail) return;
      setUserEmail(user.email);
      setValue('email', user.email);
    }
  }, [user]);

  return (
    <div className="flex flex-col gap-4">
      <Controller
        control={control}
        name={'email'}
        defaultValue={user !== null && !isGuestInviteForm && !skipUserAlreadyRegisteredCheck ? userEmail : undefined}
        rules={{
          required: {
            value: !allowEmptyEmail,
            message: t('form:fieldrequired'),
          },
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: t('auth:error-invalid-email'),
          },
        }}
        render={({ field }) => (
          <TextField
            inputRef={field.ref}
            name={field.name}
            value={field.value}
            onChange={(e) => {
              if (onChange) onChange(registrationField!._id, e.target.value);
              else field.onChange(e.target.value);
            }}
            onBlur={() => {
              if (onBlur) onBlur(registrationField!._id, field.value?.toString()!);
              else field.onBlur();
            }}
            variant={variant}
            fullWidth
            error={!!errors['email']}
            helperText={errors['email']?.message?.toString()}
            label={showFieldLabel !== false ? label : undefined}
            sx={textFieldSx}
            type="email"
            disabled={getFieldDisabledStatus()}
            id="email"
            autoComplete="email"
            inputProps={{ style: { color: fieldsTextColor || event?.template.mainColor } }}
          />
        )}
      />
      {showEmailConfirmation && (
        <Controller
          control={control}
          name={'email-confirm'}
          rules={{
            required: !allowEmptyEmail ? t('form:fieldrequired') : false,
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t('auth:error-invalid-email'),
            },
            validate: (value) => value === watch('email') || t('tickets:emails-not-match-error'),
          }}
          render={({ field }) => (
            <div className="space-y-0">
              <p className="text-base font-semibold">
                {t('auth:emailconfirm:title')}
                {!allowEmptyEmail && '*'}
              </p>
              <TextField
                inputRef={field.ref}
                name={field.name}
                onBlur={field.onBlur}
                value={field.value}
                onChange={field.onChange}
                variant={variant}
                fullWidth
                error={!!errors['email-confirm']}
                helperText={errors['email-confirm']?.message?.toString()}
                sx={textFieldSx}
                type="email"
                disabled={getFieldDisabledStatus()}
                id="email-confirm"
                inputProps={{ style: { color: fieldsTextColor || event?.template.mainColor } }}
              />
            </div>
          )}
        />
      )}
    </div>
  );
};
export default RegistrationEmailField;
